import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { clearAllStorage, getStoredItem } from "../../config/storageUtils";
import Home from "../../pages/auth/Home";
import { useAuth } from "../Auth/AuthProvider";
import { nodalOfficersRoleList } from "../../config/roleConfig";

const PrivateRoute = ({ children, roles = [], redirectPath = "/" }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    userType,
    tokenGenerated,
    loading,
    roleId,
    setUserType,
    setTokenGenerated,
    setRoleId,
  } = useAuth();
  const [isAllowed, setIsAllowed] = useState(false);
  const currentPath = location.pathname.replace(/\/+$/, "");

  // ✅ Public routes that don't require login
  const publicRoutes = [
    "/admin",
    "/login",
    "/prayatan-yojana",
    "/veer-chand-yojana",
    "/deen-dayal-yojana",
    "/tourism-policy-2023",
    "/tifc-faciliation",
  ];

  useEffect(() => {
    if (loading) return;
    // ✅ If current route is public, allow directly
    if (publicRoutes.includes(currentPath)) {
      if (tokenGenerated && userType) {
        // 🚨 Already logged in but visiting /login or other public
        if (currentPath === "/login" || currentPath === "/admin") {
          if (userType === "admin" || userType === "superadmin") {
            const rolePath = nodalOfficersRoleList.find(
              (r) => r.value === String(roleId),
            )?.defaultPath;
            navigate(rolePath || "/admin/dashboard", { replace: true });
          } else {
            navigate("/dashboard", { replace: true });
          }
          return;
        }
      }
      setIsAllowed(true);
      return;
    }

    // ✅ Special CAF case
    if (
      userType === "user" &&
      (currentPath === "/validate-otp" ||
        currentPath === "/automatic-data" ||
        currentPath === "/manual-data")
    ) {
      setIsAllowed(true);
      return;
    }

    // ❌ If no token, block access
    if (!tokenGenerated && currentPath !== "/" && currentPath !== "") {
      navigate("/", { replace: true });
      return;
    }

    if (
      userType &&
      !tokenGenerated &&
      (currentPath === "/" || currentPath === "")
    ) {
      clearAllStorage();
      setUserType(null);
      setTokenGenerated(false);
      setRoleId(null);
      navigate("/", { replace: true });
      return;
    }

    // ✅ If no userType, allow public access (like Home)
    if (!userType) {
      setIsAllowed(true);
      return;
    }

    // ✅ Admin or Superadmin should only access /admin routes
    if (
      (userType === "admin" || userType === "superadmin") &&
      !currentPath.startsWith("/admin")
    ) {
      navigate("/admin", { state: { from: location }, replace: true });
      return;
    }

    // ✅ User should not access /admin routes
    if (userType === "user" && currentPath.startsWith("/admin")) {
      navigate("/dashboard", { state: { from: location }, replace: true });
      return;
    }

    // ✅ Check if user has required role
    if (roles.length > 0 && !roles.includes(userType)) {
      navigate(redirectPath, { state: { from: location }, replace: true });
      return;
    }

    setIsAllowed(true);
  }, [
    loading,
    tokenGenerated,
    userType,
    roles,
    currentPath,
    redirectPath,
    navigate,
  ]);

  // ✅ Special case: user accesses "/"
  if (location.pathname === "/") {
    return userType ? (
      userType === "superadmin" || userType === "admin" ? (
        <Navigate to="/admin" replace />
      ) : (
        <Navigate to="/dashboard" replace />
      )
    ) : (
      <Home />
    );
  }

  return isAllowed ? children : null;
};

export default PrivateRoute;
